import { api } from "@/shared/api/baseApi";
import type {
  RequestTempAccessBody,
  TempAccessGrant,
  TempAccessOptions,
} from "@keystone/contracts/temp-access";

interface GrantRef {
  userId: string;
  grantId: string;
}

const tempAccessApi = api.enhanceEndpoints({ addTagTypes: ["TempAccess"] }).injectEndpoints({
  endpoints: (build) => ({
    listMyGrants: build.query<TempAccessGrant[], void>({
      query: () => "/temp-access/me",
      providesTags: [{ type: "TempAccess", id: "MINE" }],
    }),

    listAllGrants: build.query<TempAccessGrant[], void>({
      query: () => "/temp-access",
      providesTags: [{ type: "TempAccess", id: "ALL" }],
    }),

    tempAccessOptions: build.query<TempAccessOptions, void>({
      query: () => "/temp-access/options",
      providesTags: [{ type: "TempAccess", id: "OPTIONS" }],
    }),

    // listAllGrants is served from Auth0's user search, whose index lags metadata
    // writes by a few seconds. Invalidating ALL would refetch stale data, so the
    // mutations below patch that cache manually instead of invalidating it.
    requestAccess: build.mutation<TempAccessGrant, RequestTempAccessBody>({
      query: (body) => ({ url: "/temp-access/requests", method: "POST", body }),
      invalidatesTags: [
        { type: "TempAccess", id: "MINE" },
        { type: "TempAccess", id: "OPTIONS" },
      ],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(
            tempAccessApi.util.updateQueryData("listAllGrants", undefined, (draft) => {
              if (!draft.some((g) => g.id === data.id)) draft.push(data);
            }),
          );
        } catch {}
      },
    }),

    approveAccess: build.mutation<TempAccessGrant, GrantRef>({
      query: ({ userId, grantId }) => ({
        url: `/temp-access/${userId}/requests/${grantId}/approve`,
        method: "POST",
      }),
      invalidatesTags: [
        { type: "TempAccess", id: "MINE" },
        { type: "TempAccess", id: "OPTIONS" },
      ],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(
            tempAccessApi.util.updateQueryData("listAllGrants", undefined, (draft) => {
              const i = draft.findIndex((g) => g.id === data.id);
              if (i !== -1) draft[i] = data;
            }),
          );
        } catch {}
      },
    }),

    revokeAccess: build.mutation<TempAccessGrant, GrantRef>({
      query: ({ userId, grantId }) => ({
        url: `/temp-access/${userId}/requests/${grantId}/revoke`,
        method: "POST",
      }),
      invalidatesTags: [
        { type: "TempAccess", id: "MINE" },
        { type: "TempAccess", id: "OPTIONS" },
      ],
      async onQueryStarted({ grantId }, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(
            tempAccessApi.util.updateQueryData("listAllGrants", undefined, (draft) =>
              draft.filter((g) => g.id !== grantId),
            ),
          );
        } catch {}
      },
    }),
  }),
});

export const {
  useListMyGrantsQuery,
  useListAllGrantsQuery,
  useTempAccessOptionsQuery,
  useRequestAccessMutation,
  useApproveAccessMutation,
  useRevokeAccessMutation,
} = tempAccessApi;
export { tempAccessApi };
